// src/pages/RoomsOverview/RoomsOverview.tsx
import React, { useState, useEffect, Fragment, FunctionComponent } from "react";
import { useParams } from "react-router-dom";
import moment from "moment";
import parse from "html-react-parser";
import styles from "./styles.module.sass";
import Page from "../../layouts/Page";
import RoomGroup from "../../components/RoomGroup";
import TextBlock from "../../components/TextBlock";
import Button from "../../components/Button";
import Carousel from "../../components/Carousel";
import data from "./rooms.json";
import baliData from "./rooms-bali.json";
import klData from "./rooms-kl.json";
import { hostname } from "../../constants";

// Load icon URLs via import
import Bed2 from "./icons/bed-2.svg";
import DoubleFlex from "./icons/double-flex.svg";
import Double from "./icons/double.svg";
import Single from "./icons/single.svg";
import Bathtub from "./icons/bathtub.svg";
import Garden from "./icons/garden.svg";
import Fridge from "./icons/fridge.svg";
import Shower from "./icons/shower.svg";
import Tv from "./icons/tv.svg";
import Wifi from "./icons/wifi.svg";
import Desk from "./icons/desk.svg";
import Dining from "./icons/dining.svg";

const icons: Record<
  string,
  FunctionComponent<React.SVGProps<SVGSVGElement>>
> = {
  double_flex: DoubleFlex,
  double: Double,
  single: Single,
  bathtub: Bathtub,
  dining: Dining,
  garden: Garden,
  desk: Desk,
  fridge: Fridge,
  shower: Shower,
  tv: Tv,
  wifi: Wifi,
};

// Define the params type
interface RouteParams {
  country?: string;
  type?: string;
}

const RoomsOverview: React.FC = () => {
  // Use typed params
  const { country = "", type = "" } =
    useParams<Record<string, string | undefined>>();
  const [room, setRoom] = useState<any>(null);
  const [content, setContent] = useState<string>("");

  useEffect(() => {
    if (!country || !type) {
      return;
    }

    const fetchUrl = `${hostname}/${country}/wp-json/wp/v2/rooms?_embed&slug=${type}`;
    fetch(fetchUrl)
      .then((res) => res.json())
      .then((json: any[]) => {
        if (json && json.length > 0) {
          const first = json[0];
          setRoom(first);
          setContent(first.content.rendered);
        } else {
          // console.warn("No room data found");
        }
      })
      .catch((error) => {
        console.error("Error fetching room data:", error);
      });
  }, [country, type]);

  const getRoomGroup = () => {
    const groups =
      country === "singapore"
        ? data
        : country === "bali"
        ? baliData
        : country === "kuala-lumpur"
        ? klData
        : [];
    const base = country === "singapore" ? 8 : country === "bali" ? 7 : 10;
    return groups.map((group) => (
      <RoomGroup
        key={group.category}
        active={type}
        country={country}
        category={group.category}
        base={base}
        rooms={group.rooms}
      />
    ));
  };

  const getBookNowLink = () => {
    if (!room || !room.acf || !room.acf.book_now_link) return "";
    const link = room.acf.book_now_link;
    if (country === "singapore") {
      const params = new URLSearchParams({
        checkInDate: moment().format("YYYY-MM-DD"),
        checkOutDate: moment().add(1, "days").format("YYYY-MM-DD"),
        adults: "2",
        children: "0",
      });
      return `${link}?${params.toString()}`;
    }
    return link;
  };

  return (
    <Page type="local" className={styles.base} country={country}>
      <div className={styles.roomNav}>{getRoomGroup()}</div>
      {room && (
        <Fragment>
          {room.id && <Carousel data={room} />}
          <div className={styles.details}>
            <TextBlock>
              <Fragment>{parse(content)}</Fragment>
              <p>{room.acf?.room_specifications}</p>
            </TextBlock>
            <Button href={getBookNowLink()}>Book now</Button>
            <div className={styles.facilities}>
              {room.acf?.room_facilities?.map((fac: string) => {
                const Icon = icons[fac];
                return Icon ? (
                  <Icon
                    key={fac}
                    className={styles.icon}
                    aria-label={fac.replace("_", " ")}
                  />
                ) : null;
              })}
            </div>
          </div>
        </Fragment>
      )}
    </Page>
  );
};

export default RoomsOverview;
